/**
 * SHELL — authentication: anonymous-first, claim-to-upgrade.
 *
 * Anonymous-first means the very first run requires ZERO signup: we create a
 * Supabase ANONYMOUS session on boot and play immediately. The player only
 * converts to a real account when they choose to "claim their streak", which
 * LINKS an email/OAuth identity to the existing anonymous user — preserving
 * their streak and scores.
 *
 * The session JWT is what the network client sends as `Authorization: Bearer`.
 *
 * Graceful degradation: if Supabase isn't configured (no env), we still produce
 * a stable local "offline" identity so the whole shell runs and you can play +
 * see your cached streak. Submission simply has no token (Backend will 401/501,
 * which the client already renders as a pending state).
 *
 * The Supabase SDK is dynamically imported so a misconfigured/offline build does
 * not pull auth code into the critical path and never hard-fails boot.
 */

import { hasSupabase, readSupabaseConfig } from '../config';
import type { SupabaseConfig } from '../config';

/** A minimal structural view of the bits of the Supabase client we use. We avoid
 *  a hard import-type dependency so the SDK stays fully lazy and optional. */
interface SupabaseAuthLike {
  getSession(): Promise<{ data: { session: SessionLike | null } }>;
  signInAnonymously(): Promise<{ data: { session: SessionLike | null }; error: unknown }>;
  onAuthStateChange(cb: (event: string, session: SessionLike | null) => void): {
    data: { subscription: { unsubscribe(): void } };
  };
  signInWithOtp(creds: { email: string }): Promise<{ error: unknown }>;
  signInWithOAuth(creds: { provider: string; options?: { redirectTo?: string } }): Promise<{ error: unknown }>;
  updateUser(attrs: { email?: string }): Promise<{ error: unknown }>;
  signOut(): Promise<{ error: unknown }>;
}
interface SupabaseClientLike {
  auth: SupabaseAuthLike;
}
interface SessionLike {
  access_token: string;
  user: { id: string; is_anonymous?: boolean; email?: string | null };
}

export type AuthKind = 'anonymous' | 'claimed' | 'offline';

export interface AuthIdentity {
  readonly kind: AuthKind;
  readonly userId: string;
  readonly displayName: string;
  readonly email: string | null;
  /** Present only for real Supabase sessions; the network client sends it. */
  readonly accessToken: string | null;
}

export interface AuthClient {
  /** Current identity (always defined after init). */
  current(): AuthIdentity;
  /** Fresh bearer token for an API call, or null when offline/unauthenticated. */
  getToken(): Promise<string | null>;
  /** Begin claiming the anonymous account via an email magic link. */
  claimWithEmail(email: string): Promise<{ ok: boolean; error?: string }>;
  /** Begin claiming via an OAuth provider (redirect flow). */
  claimWithOAuth(provider: 'google' | 'apple'): Promise<{ ok: boolean; error?: string }>;
  /** Subscribe to identity changes; returns an unsubscribe fn. */
  subscribe(listener: (id: AuthIdentity) => void): () => void;
  signOut(): Promise<void>;
}

const OFFLINE_ID_KEY = 'dailyroll:offlineId';

function offlineIdentity(): AuthIdentity {
  let id: string;
  try {
    id = localStorage.getItem(OFFLINE_ID_KEY) ?? '';
    if (!id) {
      id = `offline-${crypto.randomUUID()}`;
      localStorage.setItem(OFFLINE_ID_KEY, id);
    }
  } catch {
    id = `offline-${Math.random().toString(36).slice(2)}`;
  }
  return { kind: 'offline', userId: id, displayName: 'You', email: null, accessToken: null };
}

function nameFor(session: SessionLike): string {
  if (session.user.email) return session.user.email.split('@')[0] ?? 'Player';
  return 'You';
}

function toIdentity(session: SessionLike | null): AuthIdentity {
  if (!session) return offlineIdentity();
  const claimed = session.user.is_anonymous === false;
  return {
    kind: claimed ? 'claimed' : 'anonymous',
    userId: session.user.id,
    displayName: nameFor(session),
    email: session.user.email ?? null,
    accessToken: session.access_token,
  };
}

/** Create the auth client. Always resolves; never throws on a bad config. */
export async function createAuthClient(cfg: SupabaseConfig = readSupabaseConfig()): Promise<AuthClient> {
  const listeners = new Set<(id: AuthIdentity) => void>();
  let identity: AuthIdentity = offlineIdentity();
  let client: SupabaseClientLike | null = null;

  const emit = (): void => {
    for (const l of listeners) l(identity);
  };

  if (hasSupabase(cfg)) {
    try {
      const mod = await import('@supabase/supabase-js');
      client = mod.createClient(cfg.url, cfg.anonKey, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
      }) as unknown as SupabaseClientLike;

      const existing = await client.auth.getSession();
      let session = existing.data.session;
      if (!session) {
        const created = await client.auth.signInAnonymously();
        session = created.data.session;
      }
      identity = toIdentity(session);

      client.auth.onAuthStateChange((_event, s) => {
        identity = toIdentity(s);
        emit();
      });
    } catch {
      // Network/SDK failure: stay offline-anonymous so the game still runs.
      client = null;
      identity = offlineIdentity();
    }
  }

  return {
    current: () => identity,

    async getToken() {
      if (!client) return identity.accessToken;
      try {
        const { data } = await client.auth.getSession();
        return data.session?.access_token ?? null;
      } catch {
        return null;
      }
    },

    async claimWithEmail(email) {
      if (!client) return { ok: false, error: 'Accounts are unavailable offline. Try again when connected.' };
      const trimmed = email.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) return { ok: false, error: 'Enter a valid email address.' };
      try {
        // For an anonymous user, updating the email + OTP links the account,
        // preserving the existing user id (and thus the streak).
        const { error } = identity.kind === 'anonymous'
          ? await client.auth.updateUser({ email: trimmed })
          : await client.auth.signInWithOtp({ email: trimmed });
        if (error) return { ok: false, error: 'Could not send the link. Please try again.' };
        return { ok: true };
      } catch {
        return { ok: false, error: 'Network error. Please try again.' };
      }
    },

    async claimWithOAuth(provider) {
      if (!client) return { ok: false, error: 'Accounts are unavailable offline.' };
      try {
        const { error } = await client.auth.signInWithOAuth({
          provider,
          options: { redirectTo: window.location.origin },
        });
        if (error) return { ok: false, error: 'Could not start sign-in.' };
        return { ok: true };
      } catch {
        return { ok: false, error: 'Network error.' };
      }
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    async signOut() {
      if (client) {
        try {
          await client.auth.signOut();
        } catch {
          /* ignore */
        }
      }
      identity = offlineIdentity();
      emit();
    },
  };
}
